/**
 * colors.js — central color palette for messenger-web.
 *
 * Single source of truth for all color values. Use these tokens in
 * StyleSheet.create blocks and inline color props instead of literals:
 *
 *   backgroundColor: colors.surface
 *   <ActivityIndicator color={colors.accent} />
 *
 * Keep the --chat-* CSS variables in web/chat-screen.css in sync with
 * the dark-theme surface tokens below (bg / surface / border).
 */

export const colors = {
  // ── Brand ──────────────────────────────────────────────────────────
  accent: '#e94560',
  accentDark: '#d63f6f',
  accentAlt: '#e94477',
  accentA50: '#e9456080',
  accentA15: 'rgba(233, 69, 96, 0.15)',
  accentA20: 'rgba(233, 69, 96, 0.2)',

  // ── Dark theme surfaces ────────────────────────────────────────────
  bg: '#1a1a2e',
  bgDeep: '#0f0f23',
  bgBand: '#1a1a3e',
  surface: '#16213e',
  surfaceAlt: '#1e1e3f',
  surfaceDeep: '#0f1b36',
  surfaceInset: '#121b34',
  surfacePanel: '#11244f',
  surfaceSteel: '#1a2b4f',
  surfaceNavy: '#1b3567',
  surfaceBlue: '#18457f',

  // ── Borders (dark theme) ───────────────────────────────────────────
  border: '#0f3460',
  borderSoft: '#1f2a4d',
  borderMid: '#2a3a61',
  borderDeep: '#1e3a5f',
  borderSlate: '#4b5f91',
  borderIndigo: '#2d2d5a',

  // ── Text (dark theme) ──────────────────────────────────────────────
  textSoft: '#f5f5ff',
  textMuted: '#8892b0',
  textSlate: '#a3a3c2',
  textLavender: '#d1d9ee',
  textDim: '#888',
  textDimmer: '#666',
  textPlaceholder: '#999',
  textSubtle: '#aaa',
  textGray400: '#9ca3af',
  textGray300: '#cbd5e1',
  textGray200: '#e2e8f0',
  textBright: '#f8fafc',
  textPlaceholderBlue: '#6b7490',
  textA80: 'rgba(255, 255, 255, 0.8)',
  textA60: 'rgba(255, 255, 255, 0.6)',
  textA40: 'rgba(255, 255, 255, 0.4)',

  // ── Neutrals (multi-role: text / bg / border) ──────────────────────
  white: '#fff',
  blackA50: 'rgba(0, 0, 0, 0.5)',
  blackA55: 'rgba(0, 0, 0, 0.55)',
  blackA70: 'rgba(0, 0, 0, 0.7)',
  gray300: '#d1d5db',
  gray500: '#6b7280',
  gray600: '#4b5563',

  // ── Light theme (NewChatScreen, login inputs) ──────────────────────
  lightBg: '#f5f5f5',
  lightInputBg: '#f9fafb',
  lightChipBg: '#f3f4f6',
  lightBorder: '#e0e0e0',
  lightBorderSoft: '#e5e7eb',
  lightText: '#333',
  lightTextStrong: '#111827',
  lightChipActive: '#e8eefb',
  lightAccent: '#6C3FA0',

  // ── Status: success / online ───────────────────────────────────────
  success: '#22c55e',
  successSoft: '#86efac',
  successDeep: '#10b981',
  successAlt: '#34d399',
  successSurface: '#0f3d1e',
  successA15: 'rgba(34, 197, 94, 0.15)',
  successA18: 'rgba(34, 197, 94, 0.18)',
  successA55: 'rgba(34, 197, 94, 0.55)',

  // ── Status: danger / error ─────────────────────────────────────────
  danger: '#ef4444',
  dangerDark: '#dc2626',
  dangerSoft: '#fca5a5',
  dangerAlt: '#f87171',
  dangerBorder: '#fb7185',
  dangerSurface: '#3d0f0f',
  dangerSurfaceAlt: '#3a1d33',
  dangerA12: 'rgba(239, 68, 68, 0.12)',
  dangerA15: 'rgba(239, 68, 68, 0.15)',
  dangerA18: 'rgba(239, 68, 68, 0.18)',
  dangerA40: 'rgba(239, 68, 68, 0.4)',
  dangerA50: 'rgba(239, 68, 68, 0.5)',
  dangerA55: 'rgba(239, 68, 68, 0.55)',

  // ── Status: warning ────────────────────────────────────────────────
  warning: '#f59e0b',
  warningSoft: '#fde68a',
  warningAlt: '#facc15',
  warningA15: 'rgba(245, 158, 11, 0.15)',
  warningA18: 'rgba(245, 158, 11, 0.18)',
  warningA55: 'rgba(245, 158, 11, 0.55)',
  amberA12: 'rgba(251, 191, 36, 0.12)',
  amberA35: 'rgba(251, 191, 36, 0.35)',
  yellowA15: 'rgba(234, 179, 8, 0.15)',
  yellowA40: 'rgba(234, 179, 8, 0.4)',

  // ── Status: info / links ───────────────────────────────────────────
  info: '#3b82f6',
  infoSoft: '#60a5fa',
  infoFaint: '#bfdbfe',
  infoPale: '#dbeafe',
  infoA15: 'rgba(59, 130, 246, 0.15)',
  infoA18: 'rgba(59, 130, 246, 0.18)',
  infoA55: 'rgba(59, 130, 246, 0.55)',
  infoSoftA75: 'rgba(96, 165, 250, 0.75)',
  infoRingA75: 'rgba(147, 197, 253, 0.75)',

  // ── Indigo / violet accents ────────────────────────────────────────
  indigo: '#6366f1',
  indigoDark: '#4f46e5',
  indigoSoft: '#c7d2fe',
  indigoA10: 'rgba(99, 102, 241, 0.1)',
  indigoA18: 'rgba(99, 102, 241, 0.18)',
  indigoA55: 'rgba(99, 102, 241, 0.55)',

  // ── Pink accents (female gender ring / card) ───────────────────────
  pink: '#ec4899',
  pinkSoft: '#f472b6',
  pinkA75: 'rgba(244, 114, 182, 0.75)',

  // ── Emerald accents ────────────────────────────────────────────────
  emeraldA12: 'rgba(16, 185, 129, 0.12)',
  emeraldA14: 'rgba(16, 185, 129, 0.14)',
  emeraldA25: 'rgba(16, 185, 129, 0.25)',
  emeraldA40: 'rgba(16, 185, 129, 0.4)',
  emeraldA45: 'rgba(16, 185, 129, 0.45)',
  emeraldA70: 'rgba(16, 185, 129, 0.7)',

  // ── Slate / gray alpha overlays ────────────────────────────────────
  gray400A15: 'rgba(156, 163, 175, 0.15)',
  slateA10: 'rgba(148, 163, 184, 0.10)',
  slateA15: 'rgba(148, 163, 184, 0.15)',
  slateA25: 'rgba(148, 163, 184, 0.25)',
  slateA35: 'rgba(148, 163, 184, 0.35)',
  slateA45: 'rgba(148, 163, 184, 0.45)',

  // ── Overlays / scrims ──────────────────────────────────────────────
  overlayFaint: 'rgba(255, 255, 255, 0.08)',
  overlaySoft: 'rgba(255, 255, 255, 0.12)',
  scrimA22: 'rgba(9, 20, 46, 0.22)',
  scrimA40: 'rgba(9, 20, 46, 0.40)',
  scrimA68: 'rgba(6, 10, 20, 0.68)',
  scrimA85: 'rgba(10, 18, 41, 0.85)',
  navyA22: 'rgba(17, 36, 79, 0.22)',
  pillBgA62: 'rgba(30, 64, 175, 0.62)',
  pillBorderA55: 'rgba(30, 58, 138, 0.55)',
};

export default colors;
